import { useEffect, useRef, useState } from 'react'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { SearchField } from '../../platform/primitives/SearchField'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { TYPE_LABEL, type MerchantFilters, type MerchantType } from './merchantFilters'
import './merchants.css'

/** The same glyph `CategoryTable.tsx`'s own search pill draws, repeated
 *  rather than imported for the reason `Merchants.tsx`'s `PencilIcon`
 *  already gives: a route's presentational control, not a shared
 *  primitive. */
/** How long a keystroke waits before it becomes a request — Task 4's brief,
 *  verbatim. Mutation-tested to 0 in the report; at 0 the debounce test goes
 *  red, which is the point of that step. */
const DEBOUNCE_MS = 300

/** "All types" plus the five API values, in the order the design's own type
 *  filter shows them — `null` stands for "no filter" the same way
 *  `MerchantFilters.type` already represents it. */
const TYPE_FILTER_VALUES: (MerchantType | null)[] = [null, ...(Object.keys(TYPE_LABEL) as MerchantType[])]

/** The "no type filter" option's value. A sentinel STRING, because a
 *  `SegmentedControl` option value is a string and `null` is not one — mapped
 *  back to `null` the moment it leaves the control. */
const ALL = 'all'

type Props = {
  filters: MerchantFilters
  /** A full next-`MerchantFilters`, the same one-action shape `Merchants.tsx`'s
   *  reducer already takes — every control here resets `page` to 1 itself
   *  rather than relying on the caller to notice a narrower result set. */
  onChange: (next: MerchantFilters) => void
}

/**
 * Search, the type filter and "Show deleted" — Task 4's controls. "New
 * merchant" moved to the page header (`Merchants.tsx`) to match the design
 * that arrived 2026-08-21, the same placement `Categories.tsx` already uses.
 *
 * Shape and skin follow `CategoryTable.tsx`'s own toolbar (the search pill,
 * `--chip`/`--edge` capsules) where the design has not since said otherwise.
 *
 * **Renders no outer wrapper of its own** since 2026-08-21 (wave 5, D4) —
 * `Merchants.tsx` owns the single toolbar ROW now (title, these controls,
 * "New merchant" together), so this returns its two groups
 * (`.merchants-toolbar__left`, `.merchants-toolbar__field`) as siblings in a
 * Fragment, landing as direct flex children of that row rather than of a box
 * this component used to draw around them.
 *
 * **Drops the type filter and "Show deleted" below 640px**
 * (`useBreakpoint() === 'phone'`), rather than letting the toolbar row wrap
 * onto extra lines — the same "drop columns, don't restack" precedent
 * `Merchants.tsx`'s own `shown` (dropping the Type/Notes grid columns on a
 * phone) already set for this module, M11-R21 as amended. Search survives
 * unconditionally: it is the one control that answers "where is the merchant
 * I'm looking for", the same question the grid's surviving Merchant column
 * answers for the row itself. The real cost, stated rather than hidden: a
 * phone-width user cannot narrow by type or reach "Show deleted" at all
 * today — there is nowhere else in this row to put them once the row itself
 * cannot hold five items, and inventing a second surface (a sheet, a menu)
 * for two controls was judged a bigger design decision than this task's
 * brief asked for. Widening the viewport is the only way back to them right
 * now.
 */
export function MerchantsToolbar({ filters, onChange }: Props) {
  const target = useHitTarget('page')
  const phone = useBreakpoint() === 'phone'

  // Local echo of `filters.search`, so the box can show every keystroke
  // immediately while the request itself waits out the debounce below.
  const [search, setSearch] = useState(filters.search)

  /**
   * Resync on the whole `filters` IDENTITY, not on `filters.search` alone —
   * correctness review I2.
   *
   * "Clear search" dispatches `DEFAULT_FILTERS`, whose `search` is `''`. If the
   * user had typed but the debounce had not yet committed, `filters.search` was
   * ALSO still `''`, so a `[filters.search]` dependency saw no change, the draft
   * kept the typed text, and the armed timer then fired and re-applied it — the
   * button cleared the list and the box refilled a moment later.
   *
   * The cost, stated: typing and then touching another control discards the
   * uncommitted keystrokes rather than committing them late. That is the right
   * way round — the committed state is what the list is showing, and a filter
   * the user just clicked must not be undone by a timer they cannot see.
   */
  useEffect(() => setSearch(filters.search), [filters])

  /* The timer reads the CURRENT filters, never the ones captured when it was
     armed. Spreading a stale `filters` was the other half of I2: a timer armed
     before a type-filter click would fire afterwards and splice the old type
     back in, silently reverting a change the user had made in between. */
  const latest = useRef({ filters, onChange })
  latest.current = { filters, onChange }

  useEffect(() => {
    // No-op on mount, and on every render where typing has caught back up
    // with the committed filter — e.g. the debounced call itself lands and
    // sets `filters.search` to the same value this echoed already.
    if (search === latest.current.filters.search) return
    const id = setTimeout(() => {
      // Changing search resets to page 1 — a stale page number over a newly
      // narrow result set would request a page that no longer exists.
      latest.current.onChange({ ...latest.current.filters, search, page: 1 })
    }, DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  return (
    <>
      {/* The search field and "Show deleted" — one group, so both share a
          gap distinct from the toolbar row's own (search survives every
          breakpoint; "Show deleted" does not — see the docblock above). */}
      <div className="merchants-toolbar__left">
        <SearchField
          className="merchants-toolbar__search"
          label="Search merchants"
          placeholder="Search merchants"
          value={search}
          onChange={setSearch}
        />

        {/* A real switch, not a native checkbox — a bare `<input
            type="checkbox">` ships the OS accent colour and no colour or
            contrast sweep can see an absent style declaration (this repo has
            shipped that defect twice). `role="switch"` plus `aria-checked`
            instead, skinned entirely in `merchants.css`. The visible text IS
            the accessible name, so there is nothing for a label and an
            aria-label to disagree about. Dropped below 640px — see this
            component's own docblock. */}
        {!phone && (
          <button
            type="button"
            role="switch"
            aria-checked={filters.includeInactive}
            className={`merchants-toolbar__switch${
              filters.includeInactive ? ' merchants-toolbar__switch--on' : ''
            }`}
            style={{ minHeight: target }}
            onClick={() =>
              onChange({ ...filters, includeInactive: !filters.includeInactive, page: 1 })
            }
          >
            <span className="merchants-toolbar__switch-track" aria-hidden="true">
              <span className="merchants-toolbar__switch-thumb" />
            </span>
            <span className="merchants-toolbar__switch-text">Show deleted</span>
          </button>
        )}
      </div>

      {/* A real radiogroup, not a native `<select>`. A bare `<select>`
          renders its closed box and its option list in OS chrome, and
          neither carries a CSS declaration a colour or contrast sweep can
          read — an absent declaration is unscannable, and this repo has
          shipped that defect twice (`.merchants-toolbar__switch` above is
          the first fix of the pair). Real, visually-hidden `<input
          type="radio">` elements — the same mechanism `MerchantForm.tsx`'s
          own Type segment already uses for this exact five-value set — get
          native keyboard operation (Tab into the group, arrow keys between
          options) for free, and every pixel a user or a sweep sees is a
          class in `merchants.css`. Dropped below 640px — see this
          component's own docblock. */}
      {!phone && (
        <span className="merchants-toolbar__field">
          <span className="merchants-toolbar__label" id="merchants-toolbar-type-label">
            Type
          </span>
          {/* `ALL` stands in for `null` across the wire boundary: a
              `SegmentedControl` value is a string, and `null` is not one. It
              is mapped straight back on change, so the filter still sends an
              absent `type` rather than the literal "all". */}
          <SegmentedControl
            label="Type"
            name="merchants_type_filter"
            options={TYPE_FILTER_VALUES.map((value) => ({
              value: value ?? ALL,
              label: value === null ? 'All types' : TYPE_LABEL[value],
            }))}
            value={filters.type ?? ALL}
            onChange={(next) =>
              onChange({ ...filters, type: next === ALL ? null : (next as MerchantType), page: 1 })
            }
          />
        </span>
      )}
    </>
  )
}
